// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, Settings } from 'lucide-react'
import * as api from '../api/client'

/**
 * In-app notification bell with unread badge + dropdown feed. Pharmacy users only.
 * Polls unread count every 60s. PHI-free by contract (server never stores PHI in notifications).
 */
export default function NotificationBell() {
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<api.NotificationItem[]>([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const refreshCount = useCallback(async () => {
    try { const r = await api.getNotifications(false, 1); setUnread(r.unread_count) }
    catch { /* silent — bell is non-critical */ }
  }, [])

  const loadFeed = useCallback(async () => {
    setLoading(true)
    try { const r = await api.getNotifications(false, 30); setItems(r.items); setUnread(r.unread_count) }
    catch { /* silent */ }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { void refreshCount(); const t = setInterval(refreshCount, 60_000); return () => clearInterval(t) }, [refreshCount])
  useEffect(() => { if (open) void loadFeed() }, [open, loadFeed])
  useEffect(() => {
    function onDoc(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    if (open) document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  async function markAllRead() {
    try { await api.markNotificationsRead({ all: true }); setItems(i => i.map(n => ({ ...n, is_read: true }))); setUnread(0) }
    catch { /* silent */ }
  }
  async function openItem(n: api.NotificationItem) {
    if (!n.is_read) { try { await api.markNotificationsRead({ ids: [n.id] }); setUnread(u => Math.max(0, u - 1)) } catch { /* silent */ } }
    setOpen(false)
    if (n.link) nav(n.link)
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
              aria-haspopup="menu" aria-expanded={open} style={btn} title="Notifications">
        <Bell size={16} aria-hidden="true" />
        {unread > 0 && <span style={badge} aria-hidden="true">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div role="menu" style={panel}>
          <div style={panelHead}>
            <span style={panelTitle}>Notifications</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {unread > 0 && (
                <button onClick={markAllRead} style={headBtn} title="Mark all read"><Check size={13} /> Mark all read</button>
              )}
              <button onClick={() => { setOpen(false); nav('/settings/notifications') }} style={headBtn} title="Notification settings"><Settings size={13} /></button>
            </div>
          </div>
          <div style={feed}>
            {loading ? (
              <div style={emptyState}>Loading…</div>
            ) : items.length === 0 ? (
              <div style={emptyState}>You're all caught up.</div>
            ) : (
              items.map(n => (
                <button key={n.id} onClick={() => openItem(n)} style={{ ...row, ...(n.is_read ? null : rowUnread) }}>
                  {!n.is_read && <span style={dot} aria-hidden="true" />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={rowTitle}>{n.title}</div>
                    {n.body && <div style={rowBody}>{n.body}</div>}
                    <div style={rowTime}>{timeAgo(n.created_at)}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function timeAgo(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

const btn: React.CSSProperties = { position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 8, border: 'none', background: 'transparent', color: '#475569', cursor: 'pointer' }
const badge: React.CSSProperties = { position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, background: '#dc2626', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }
const panel: React.CSSProperties = { position: 'absolute', top: 40, right: 0, width: 340, maxHeight: 460, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 8px 30px rgba(15,23,42,0.15)', zIndex: 50, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
const panelHead: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #f1f5f9' }
const panelTitle: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: '#0f172a' }
const headBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: '#0e7490', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6 }
const feed: React.CSSProperties = { overflowY: 'auto', flex: 1 }
const emptyState: React.CSSProperties = { padding: 28, textAlign: 'center', color: '#94a3b8', fontSize: 13 }
const row: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', textAlign: 'left', padding: '11px 14px', border: 'none', borderBottom: '1px solid #f8fafc', background: 'none', cursor: 'pointer' }
const rowUnread: React.CSSProperties = { background: '#f0f9ff' }
const dot: React.CSSProperties = { width: 7, height: 7, borderRadius: '50%', background: '#0891b2', marginTop: 5, flexShrink: 0 }
const rowTitle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#1e293b' }
const rowBody: React.CSSProperties = { fontSize: 12, color: '#64748b', marginTop: 1, lineHeight: 1.4 }
const rowTime: React.CSSProperties = { fontSize: 11, color: '#94a3b8', marginTop: 3 }
